import Head from "next/head";
import { useRouter } from "next/router";
import { useStore } from "../../stores/use_global_state";
import getCommonLocales from "../../locales/locales_common";
import getErrorLocales from "../../locales/locales_error";
import getRemoveBgPageLocales from "../../locales/locales_remove_bg";
import EditorLanding from "../../components/imgEditor/EditorLanding";
import Layout from "../../components/layout";
import { photoFileExtensionList } from "../../constants/file_extension";
import dynamic from "next/dynamic";
import { removeFromLocalStorage } from "../../utils/nonAuthLocalStorage";

const DynamicImgEditor = dynamic(() => import("../../components/imgEditor"), { ssr: false });

export default function Editor({ image_url }) {
	const router = useRouter();
	const { state, dispatch } = useStore();

	const dictionary = {
		...getCommonLocales(router.locale),
		...getErrorLocales(router.locale),
		...getRemoveBgPageLocales(router.locale),
	};

	const uploadFile = async (files) => {
		let image = files[0];

		let fileExtension = image.name.split(".").pop();
		const ExtensionList = new Set(photoFileExtensionList);
		const errorMsg = dictionary["Extension .XX is not allowed. Only .mp4, .mov, .ts, .avi, .mkv, .webm, .mpg, .jpg, .png are allowed"]
			.replace("XX", fileExtension)
			.replace(" .mp4, .mov, .mkv, .webm,", "");

		if (fileExtension.toLowerCase() === "heif" || fileExtension.toLowerCase() === "heic") {
			const heic2any = (await import("heic2any")).default;

			fetch(URL.createObjectURL(image))
				.then((res) => {
					return res.blob();
				})
				.then((blob) => {
					return heic2any({ blob });
				})
				.then((conversionResult) => {
					const url = URL.createObjectURL(conversionResult);
					dispatch({ type: "setEraseAndReplaceImg", img: [url], ids: null });
				})
				.catch((e) => {
					dispatch({ type: "setVideoError", error_msg: errorMsg, error_code: "text" });
				});
			return;
		}

		if (!ExtensionList.has(fileExtension.toLowerCase())) {
			dispatch({ type: "setVideoError", error_msg: errorMsg, error_code: "text" });

			return false;
		}
		removeFromLocalStorage();
		dispatch({ type: "setEditorImage", imageData: URL.createObjectURL(image) });
		router.push("/editor/?screen=full");
	};

	const pageLink = image_url ? `https://bgrem.ai/editor/?image_url=${image_url}` : "https://bgrem.ai/editor/";

	let title = dictionary["page_editor_title"] || "AI photo editor: enhance photo with AI tools – BgRem";
	let description =
		dictionary["page_editor_description"] ||
		"With BgRem editor you can enhance your photos online and easily with AI tools. Moreover, you can resize, crop, watermark your pictures, apply filters, add stickers and text.";
	let ogTitle = title;
	let ogDescription = description;

	const getCanonicalLink = (locale) => {
		const enList = ["en", "ja", "ar"];

		if (enList.includes(locale)) {
			return `https://bgrem.ai/editor/`;
		} else {
			return `https://bgrem.ai/${locale}/editor/`;
		}
	};

	return (
		<Layout mainClassName={`remove_bg magic_bg avatar-layout ai-main`}>
			<Head>
				<title>{title}</title>
				<meta name="description" content={description} />
				<meta property="og:type" content="website" />
				<meta property="og:title" content={ogTitle} />
				<meta property="og:description" content={ogDescription} />
				<meta property="og:url" content={pageLink} />
				<meta property="og:image" content={image_url ? image_url : "https://bgrem.ai/seo/og-styling.jpg"} />
				<meta property="og:site_name" content="bgrem" />
				<meta property="og:locale" content="en_US" />
				<meta property="og:section" content="AI photo editor" />
				<meta name="twitter:title" content={ogTitle} />
				<meta name="twitter:description" content={ogDescription} />
				<meta property="twitter:card" content="summary_large_image" />
				<meta property="twitter:image:src" content={image_url ? image_url : "https://bgrem.ai/seo/og-styling.jpg"} />
				<meta name="twitter:image" content={image_url ? image_url : "https://bgrem.ai/seo/og-styling.jpg"} />
				<link rel="canonical" href={getCanonicalLink(router.locale)} />
			</Head>

			{state.editorImage === null ? <EditorLanding dictionary={dictionary} uploadFile={uploadFile} /> : <DynamicImgEditor dictionary={dictionary} />}
		</Layout>
	);
}

export async function getServerSideProps(context) {
	const image_url = context.query.image_url || null;
	return {
		props: { image_url },
	};
}
